import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import { ProductCard } from "../components/product-card";
import { Seo } from "../components/seo";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { api } from "../lib/api";
import type { Product } from "../lib/types";

export function getFeaturedProducts(products: Product[], limit = 3) {
  return [...products]
    .sort((left, right) => {
      const likeDelta = right.like_count - left.like_count;
      if (likeDelta !== 0) return likeDelta;
      const featuredDelta = Number(right.is_featured) - Number(left.is_featured);
      if (featuredDelta !== 0) return featuredDelta;
      return Date.parse(right.created_at) - Date.parse(left.created_at);
    })
    .slice(0, limit);
}

export function HomePage() {
  const productsQuery = useQuery({ queryKey: ["products"], queryFn: () => api.products() });
  const brandsQuery = useQuery({ queryKey: ["brands"], queryFn: api.brands });
  const recentlyAdded = productsQuery.data?.items ?? [];
  const featured = getFeaturedProducts(recentlyAdded);

  return (
    <>
      <Seo
        title="REVLANE | Premium Collector Platform"
        description="A premium collector platform for diecast collectors, sellers, and rare automotive miniatures."
      />
      <section className="border-b border-white/[0.08]">
        <div className="container-pad grid min-h-[620px] items-center gap-10 py-12 sm:py-16 lg:grid-cols-[1.02fr_0.98fr]">
          <div className="space-y-8">
            <Badge className="w-fit border-accent/40 bg-accent/10 text-accent">Collector-grade platform</Badge>
            <div className="space-y-4">
              <h1 className="max-w-3xl text-4xl font-semibold leading-[1.04] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                REVLANE
              </h1>
              <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
                Rare scales, verified sellers, fast browsing, and a checkout flow built for collectors who already know what they are hunting.
              </p>
              <p className="text-sm font-medium uppercase tracking-[0.2em] text-accent">
                Pan-India shipping available
              </p>
            </div>
            <div className="flex flex-wrap gap-3 pt-1">
              <Button asChild variant="outline">
                <Link to="/search">
                  <Sparkles className="h-4 w-4" />
                  Browse latest
                </Link>
              </Button>
            </div>
            <div className="grid max-w-xl grid-cols-3 gap-3 border-t border-white/[0.08] pt-5">
              <div>
                <p className="text-xl font-semibold">1:64</p>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Focused scale</p>
              </div>
              <div>
                <p className="text-xl font-semibold">Verified</p>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Seller stock</p>
              </div>
              <div>
                <p className="text-xl font-semibold">Fast</p>
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">WhatsApp order</p>
              </div>
            </div>
          </div>
          <div className="relative aspect-[1600/1163] min-w-0 overflow-hidden rounded-lg border border-white/[0.08] bg-card shadow-soft">
            <img
              src="/assets/revlane-hero.jpeg"
              alt="RevLane cinematic diecast collector scene"
              width={1600}
              height={1163}
              className="h-full w-full object-cover object-center"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              sizes="(min-width: 1024px) 49vw, calc(100vw - 2rem)"
            />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_58%,rgba(0,0,0,0.3)_100%)]" />
          </div>
        </div>
      </section>

      <main className="container-pad space-y-14 py-10 sm:py-14">
        <section className="space-y-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.22em] text-accent">Featured</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight">Featured Products</h2>
              <p className="mt-1 text-sm text-muted-foreground">Premium collector inventory with strong demand.</p>
            </div>
            <Button asChild variant="ghost">
              <Link to="/search">
                View all
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
          {productsQuery.isError ? (
            <div className="rounded-lg border border-border bg-card p-6 text-muted-foreground">
              Products could not be loaded. Confirm the API is running and refresh.
            </div>
          ) : productsQuery.isLoading ? (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((item) => (
                <Skeleton key={item} className="h-80" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {featured.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              <div className="flex justify-center pt-2">
                <Button asChild variant="outline">
                  <Link to="/search">
                    View more
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </>
          )}
        </section>

        <section className="grid gap-8 border-t border-white/[0.08] pt-10 lg:grid-cols-[0.72fr_1.28fr]">
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold tracking-tight">Trending Brands</h2>
            <div className="flex flex-wrap gap-2">
              {(brandsQuery.data ?? []).map((brand) => (
                <Link key={brand.id} to={`/search?brand=${brand.slug}`}>
                  <Badge className="bg-card/80 transition hover:border-accent hover:text-accent">{brand.name}</Badge>
                </Link>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold tracking-tight">Recently Added</h2>
            <div className="grid gap-3">
              {recentlyAdded.slice(0, 4).map((product) => (
                <Link
                  to={`/products/${product.slug}`}
                  key={product.id}
                  className="flex items-center justify-between gap-4 rounded-lg border border-white/[0.08] bg-card/85 p-4 transition duration-200 hover:border-accent/60 hover:bg-card"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{product.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {product.brand.name} / {product.scale.name}
                    </p>
                  </div>
                  <Badge>{product.quantity_remaining} left</Badge>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
